import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, Textarea } from '@/components/ui';
import { Loader2, Play, Pause, X } from '@/components/ui/icons/Icons';
import { Trash2, Plus, Mic, Upload } from 'lucide-react';
import { SpeechToAudio } from '@/components/ui/icons/Icons';
import { useI18n } from '@/context/I18nProvider';
import { cn } from '@/lib/utils';
import { ApiClient } from '@/services/api-client';
import type { TtsVoice } from '@/services/clients/tts-api-client';
import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
type Tone = 'professional' | 'friendly' | 'enthusiastic';
type BackgroundNoise = 'cars' | 'coffee' | null;
type TextToAudioModalProps = {
  trigger: React.ReactNode;
  onAudioGenerated: (audioUrl: string, s3Key: string, text: string) => void;
  disabled?: boolean;
  initialText?: string;
};
const TONE_OPTIONS: {
  value: Tone;
  icon: string;
}[] = [{
  value: 'professional',
  icon: '💼'
}, {
  value: 'friendly',
  icon: '😊'
}, {
  value: 'enthusiastic',
  icon: '🔥'
}];
const NOISE_OPTIONS: {
  value: BackgroundNoise;
  labelKey: 'tts.noiseNone' | 'tts.noiseCars' | 'tts.noiseCoffee';
  icon: string;
}[] = [{
  value: null,
  labelKey: 'tts.noiseNone',
  icon: '🔇'
}, {
  value: 'cars',
  labelKey: 'tts.noiseCars',
  icon: '🚗'
}, {
  value: 'coffee',
  labelKey: 'tts.noiseCoffee',
  icon: '☕'
}];
export const TextToAudioModal = ({
  trigger,
  onAudioGenerated,
  disabled = false,
  initialText = ''
}: TextToAudioModalProps) => {
  const {
    t
  } = useI18n();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [tone, setTone] = useState<Tone>('friendly');
  const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewS3Key, setPreviewS3Key] = useState<string | null>(null);
  const [spokenText, setSpokenText] = useState<string | null>(null);
  const [optimizeText, setOptimizeText] = useState(true);
  const [backgroundNoise, setBackgroundNoise] = useState<BackgroundNoise>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Voice management
  const [voices, setVoices] = useState<TtsVoice[]>([]);
  const [isLoadingVoices, setIsLoadingVoices] = useState(false);
  const [showAddVoice, setShowAddVoice] = useState(false);
  const [newVoiceName, setNewVoiceName] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState<File | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadVoices = useCallback(async () => {
    setIsLoadingVoices(true);
    try {
      const response = await ApiClient.tts.listVoices();
      setVoices(response.data.voices);
    } catch {
      // Silently fail — voices are optional
    } finally {
      setIsLoadingVoices(false);
    }
  }, []);
  useEffect(() => {
    if (open) {
      loadVoices();
      if (initialText) {
        setText(initialText);
      }
    }
  }, [open, loadVoices, initialText]);

  // Force the <audio> element to load when the source URL changes
  useEffect(() => {
    if (previewUrl && audioRef.current) {
      audioRef.current.load();
    }
  }, [previewUrl]);
  const handleGenerate = async () => {
    if (!text.trim()) return;
    setIsGenerating(true);
    setPreviewUrl(null);
    setPreviewS3Key(null);
    try {
      const response_0 = await ApiClient.tts.generate({
        text: text.trim(),
        tone,
        voice_id: selectedVoiceId,
        optimize_text: selectedVoiceId ? optimizeText : undefined,
        background_noise: backgroundNoise
      });
      setPreviewS3Key(response_0.data.s3_key);
      setSpokenText(response_0.data.spoken_text);
      setPreviewUrl(response_0.data.audio_url);
    } catch {
      toast.error(t('tts.generateError'));
    } finally {
      setIsGenerating(false);
    }
  };
  const handlePlayPause = () => {
    if (!audioRef.current || !previewUrl) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => toast.error(t('tts.generateError')));
    }
  };
  const handleConfirm = () => {
    if (previewUrl && previewS3Key) {
      onAudioGenerated(previewUrl, previewS3Key, text);
      resetAndClose();
    }
  };
  const resetAndClose = () => {
    setText('');
    setTone('professional');
    setSelectedVoiceId(null);
    setPreviewUrl(null);
    setPreviewS3Key(null);
    setSpokenText(null);
    setOptimizeText(true);
    setBackgroundNoise(null);
    setIsPlaying(false);
    setShowAddVoice(false);
    setNewVoiceName('');
    setRecordedAudio(null);
    setOpen(false);
  };

  // --- Voice recording ---
  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      const mimeType = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus') ? 'audio/ogg;codecs=opus' : 'audio/webm;codecs=opus';
      const ext = mimeType.includes('mp4') ? 'm4a' : mimeType.includes('ogg') ? 'ogg' : 'webm';
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType
      });
      const chunks: BlobPart[] = [];
      mediaRecorder.ondataavailable = e => chunks.push(e.data);
      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
        const blob = new Blob(chunks, {
          type: mimeType
        });
        const file = new File([blob], `voice-clone.${ext}`, {
          type: mimeType
        });
        setRecordedAudio(file);
      };
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch {
      toast.error(t('conversations.microphoneError'));
    }
  };
  const handleStopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };
  const handleFileUpload = (e_0: React.ChangeEvent<HTMLInputElement>) => {
    const file_0 = e_0.target.files?.[0];
    if (file_0) {
      setRecordedAudio(file_0);
    }
    e_0.target.value = '';
  };
  const handleCloneVoice = async () => {
    if (!newVoiceName.trim() || !recordedAudio) return;
    setIsCloning(true);
    try {
      await ApiClient.tts.cloneVoice(newVoiceName.trim(), [recordedAudio]);
      toast.success(t('tts.voiceCloned'));
      setShowAddVoice(false);
      setNewVoiceName('');
      setRecordedAudio(null);
      await loadVoices();
    } catch {
      toast.error(t('tts.voiceCloneError'));
    } finally {
      setIsCloning(false);
    }
  };
  const handleDeleteVoice = async (voiceId: string) => {
    try {
      await ApiClient.tts.deleteVoice(voiceId);
      if (selectedVoiceId === voiceId) setSelectedVoiceId(null);
      setVoices(prev => prev.filter(v => v.voice_id !== voiceId));
      toast.success(t('tts.voiceDeleted'));
    } catch {
      toast.error(t('tts.voiceDeleteError'));
    }
  };
  return <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild disabled={disabled}>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SpeechToAudio className="size-5" />
            {t('tts.title')}
          </DialogTitle>
          <DialogDescription>
            {t('tts.description')}{' '}
            <Link href="https://docs.orsay.ai/guides&tutorials/voice-cloning" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
              {t('tts.learnMore')}
              <ExternalLink className="size-3" />
            </Link>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Voice selector */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">{t('tts.voice')}</label>
              <button onClick={() => setShowAddVoice(!showAddVoice)} className="flex items-center gap-1 text-xs text-primary hover:text-primary/80">
                <Plus className="size-3" />
                {t('tts.addVoice')}
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {/* Default voice option */}
              <button onClick={() => setSelectedVoiceId(null)} className={cn('flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors', selectedVoiceId === null ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground')}>
                {t('tts.defaultVoice')}
              </button>

              {/* Cloned voices */}
              {voices.map(voice => <div key={voice.voice_id} className="group flex items-center gap-0.5">
                  <button onClick={() => setSelectedVoiceId(voice.voice_id)} className={cn('flex items-center gap-1.5 rounded-l-md border px-3 py-1.5 text-sm transition-colors', selectedVoiceId === voice.voice_id ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground')}>
                    {voice.name}
                  </button>
                  <button onClick={() => handleDeleteVoice(voice.voice_id)} className="flex h-full items-center rounded-r-md border border-l-0 border-border px-1.5 py-1.5 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100" aria-label={t('tts.deleteVoice')}>
                    <Trash2 className="size-3" />
                  </button>
                </div>)}

              {isLoadingVoices && <div className="flex items-center px-2">
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                </div>}
            </div>
          </div>

          {/* Add voice panel */}
          {showAddVoice && <div className="flex flex-col gap-3 rounded-lg border border-dashed border-primary/30 bg-primary/5 p-3">
              <p className="text-sm font-medium">{t('tts.cloneVoiceTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('tts.cloneVoiceDescription')}</p>

              <input type="text" placeholder={t('tts.voiceNamePlaceholder')} value={newVoiceName} onChange={e_1 => setNewVoiceName(e_1.target.value)} maxLength={100} className="h-9 rounded-md border border-input bg-white px-3 text-sm" />

              <div className="flex items-center gap-2">
                {!recordedAudio ? <>
                    <Button size="sm" variant={isRecording ? 'destructive' : 'outline'} onClick={isRecording ? handleStopRecording : handleStartRecording}>
                      <Mic className="mr-1.5 size-3.5" />
                      {isRecording ? t('tts.stopRecording') : t('tts.record')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}>
                      <Upload className="mr-1.5 size-3.5" />
                      {t('tts.uploadFile')}
                    </Button>
                    <input ref={fileInputRef} type="file" accept="audio/*" className="hidden" onChange={handleFileUpload} />
                  </> : <div className="flex items-center gap-1 rounded-md bg-accent px-2 py-1">
                    <span className="text-xs">{recordedAudio.name}</span>
                    <button type="button" onClick={() => setRecordedAudio(null)} className="ml-1 text-muted-foreground hover:text-foreground">
                      <X className="size-3" />
                    </button>
                  </div>}
              </div>

              <Button size="sm" onClick={handleCloneVoice} disabled={!newVoiceName.trim() || !recordedAudio || isCloning}>
                {isCloning ? <>
                    <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                    {t('tts.cloning')}
                  </> : t('tts.cloneVoice')}
              </Button>
            </div>}

          {/* Text input */}
          <Textarea value={text} onChange={e_2 => setText(e_2.target.value)} placeholder={t('tts.textPlaceholder')} maxLength={5000} className="min-h-24 resize-none text-sm" />

          {/* Tone selector */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">{t('tts.tone')}</label>
            <div className="flex gap-2">
              {TONE_OPTIONS.map(option => <button key={option.value} onClick={() => setTone(option.value)} className={cn('flex-1 flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm transition-colors', tone === option.value ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground')}>
                  <span>{option.icon}</span>
                  <span>
                    {option.value === 'professional' ? t('tts.toneProfessional') : option.value === 'friendly' ? t('tts.toneFriendly') : t('tts.toneEnthusiastic')}
                  </span>
                </button>)}
            </div>
          </div>

          {/* Optimize text toggle (only shown for cloned voices) */}
          {selectedVoiceId && <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={optimizeText} onChange={e_3 => setOptimizeText(e_3.target.checked)} className="size-4 rounded border-border accent-primary" />
                <span className="text-sm text-muted-foreground">{t('tts.optimizeTextLabel')}</span>
              </label>
            </div>}

          {/* Background noise selector */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">
              {t('tts.backgroundNoise')}
            </label>
            <div className="flex gap-2">
              {NOISE_OPTIONS.map(option_0 => <button key={option_0.value ?? 'none'} type="button" onClick={() => setBackgroundNoise(option_0.value)} className={cn('flex-1 flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm transition-colors', backgroundNoise === option_0.value ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground')}>
                  <span>{option_0.icon}</span>
                  <span>{t(option_0.labelKey)}</span>
                </button>)}
            </div>
          </div>

          {/* Generate button */}
          <Button onClick={handleGenerate} disabled={!text.trim() || isGenerating} className="w-full">
            {isGenerating ? <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                {t('tts.generating')}
              </> : t('tts.generate')}
          </Button>

          {/* Audio preview */}
          {previewUrl && <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3 rounded-md border bg-accent/50 p-3">
                <button onClick={handlePlayPause} className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {isPlaying ? <Pause className="size-4" /> : <Play className="size-4" />}
                </button>
                <div className="flex-1">
                  <p className="text-sm font-medium">{t('tts.preview')}</p>
                  <p className="text-xs text-muted-foreground">
                    {tone === 'professional' ? t('tts.toneProfessional') : tone === 'friendly' ? t('tts.toneFriendly') : t('tts.toneEnthusiastic')}
                    {selectedVoiceId ? ` · ${voices.find(v_0 => v_0.voice_id === selectedVoiceId)?.name ?? ''}` : ''}
                  </p>
                </div>
                <Button size="sm" onClick={handleConfirm}>
                  {t('tts.useAudio')}
                </Button>
                <audio ref={audioRef} src={previewUrl} onEnded={() => setIsPlaying(false)} />
              </div>
              {spokenText && <details className="rounded-md border bg-slate-50 px-3 py-2">
                  <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                    {t('tts.spokenTextLabel')}
                  </summary>
                  <p className="mt-1.5 text-xs leading-relaxed text-[#374151]">{spokenText}</p>
                </details>}
            </div>}
        </div>
      </DialogContent>
    </Dialog>;
};